import { css } from "@emotion/css";
import { getDaysInMonth } from "date-fns";
import { useEffect, useMemo, useState } from "react";
import { useLocale } from "react-aria";

import { DateSelectSegment } from "./DateSelectSegment";
import { Granularity } from "./constants";
import { Stack } from "../../../foundations/grid-layout/Stack";
import {
  localeString,
  useDateFNSLocale,
} from "../../../hooks/useDateFNSLocale";
import {
  FieldErrorsProps,
  FieldErrors,
} from "../_assets/input-wrapper/FieldErrors";
import { FormLabelRAC } from "../_assets/input-wrapper/FormLabelRAC";

export type DateSelectValue = {
  year: number;
  month: number;
  day: number;
};

export interface DateSelectorProps {
  name?: string;
  label: string;
  formatOverride?: string;
  onChange: (value: DateSelectValue) => void;
  onBlur?: () => void;
  onFocus?: () => void;
  errors?: FieldErrorsProps["children"];
  testID: string;
  value?: DateSelectValue;
  isFutureDate?: boolean;
  isDisabled?: boolean;
  isRequired?: boolean;
  granularity?: Granularity;
  fullWidth?: boolean;
  isInvalid?: boolean;
}

const now = new Date();
const currentYear = now.getUTCFullYear();

export function DateSelector(props: DateSelectorProps) {
  const { value = { day: 0, month: 0, year: 0 } } = props;
  const { locale } = useLocale();
  const { dateFNSLocale } = useDateFNSLocale(locale as localeString);
  const [formatOrder, setFormatOrder] = useState<string[]>([]);
  const [focused, setFocused] = useState<Record<string, boolean | null>>({
    year: null,
    month: null,
    day: null,
  });

  const displayNames = new Intl.DisplayNames(locale, {
    type: "dateTimeField",
  });

  // currently there's only "month" which hides day selector
  // we can extend this component to use other formats as designs roll in
  const isDayHidden = props.granularity === Granularity.month;

  const yearPlaceholder = displayNames.of("year") as string;
  const monthPlaceholder = displayNames.of("month") as string;
  const dayPlaceholder = displayNames.of("day") as string;

  useEffect(() => {
    const componentIsTouched = Object.values(focused).some(
      (focus) => focus !== null
    );
    const componentIsFocused = Object.values(focused).some(
      (focus) => focus === true
    );

    if (componentIsFocused) {
      props.onFocus && props.onFocus();
    } else if (componentIsTouched) {
      props.onBlur && props.onBlur();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focused]);

  const handleSetYear = (year: number) => {
    const daysInMonth = fetchDaysInMonth(value.month, value.year);

    props.onChange({
      ...value,
      year,
      day: value.day > daysInMonth ? 0 : value.day,
    });
  };

  const handleSetMonth = (month: number) => {
    const daysInMonth = fetchDaysInMonth(month, value.year);

    props.onChange({
      ...value,
      month,
      day: value.day > daysInMonth ? 0 : value.day,
    });
  };

  const handleSetDay = (day: number) => {
    props.onChange({ ...value, day });
  };

  const fetchDaysInMonth = (month: number, year: number) => {
    if (!month && !year) return 0;
    return getDaysInMonth(new Date(year, month - 1, 1));
  };

  // set formatOrder based on override or locale language specific ordering
  useEffect(() => {
    const format = props.formatOverride
      ? props.formatOverride.toLowerCase()
      : dateFNSLocale?.formatLong?.date({ width: "short" }).toLowerCase();

    // remove whitespaces and split by various locale seperating chars
    // ex: "hu-HU": "yyyy. MM. dd." or "es-PA": "MM/dd/yyyy"
    setFormatOrder(
      format
        .replace(/\s/g, "") // remove spaces
        .replace(/([ymd])(?=.*\1)/g, "") // remove repeated characters
        .split(/[.,/-]/) // split into array for ordering
    );
  }, [props.formatOverride, dateFNSLocale?.formatLong]);

  // calculates and fills array with 100 based on isFutureDate props
  const years = useMemo(() => {
    return Array.from({ length: 100 }, (_, idx) => {
      if (props.isFutureDate) return currentYear + idx;
      else return currentYear - idx;
    });
  }, [props.isFutureDate]);

  // builds month array with locale specific months
  const months = useMemo(
    () =>
      Array.from({ length: 12 }, (_, idx) => {
        const month = dateFNSLocale?.localize?.month(idx);
        return month.charAt(0).toUpperCase() + month.slice(1);
      }),
    [dateFNSLocale?.localize]
  );

  // builds days array based on month and year selected
  const days = useMemo(() => {
    const daysInMonth =
      value.year && value.month
        ? fetchDaysInMonth(value.month, value.year)
        : 31;

    return Array.from({ length: daysInMonth }, (_, idx) => idx + 1);
  }, [value]);

  const segmentLookup = (input: string, index: number) => {
    switch (input) {
      case "m":
        return (
          <div key={index} className={styles.month}>
            <DateSelectSegment
              testID={`${props.testID}-month`}
              isDisabled={props.isDisabled}
              selectedKey={value?.month}
              input={input}
              setValue={handleSetMonth}
              options={months}
              placeholder={
                monthPlaceholder?.charAt(0).toUpperCase() +
                monthPlaceholder?.slice(1)
              }
              isInvalid={
                props.isInvalid || (focused["month"] === false && !value.month)
              }
              onFocus={() => setFocused((curr) => ({ ...curr, month: true }))}
              onBlur={() =>
                setTimeout(() =>
                  setFocused((curr) => ({ ...curr, month: false }))
                )
              }
              isRequired={props.isRequired}
            />
          </div>
        );
      case "d":
        if (isDayHidden) return null;
        return (
          <div key={index}>
            <DateSelectSegment
              testID={`${props.testID}-day`}
              isDisabled={props.isDisabled}
              selectedKey={value.day}
              input={input}
              setValue={handleSetDay}
              options={days}
              placeholder={
                dayPlaceholder?.charAt(0).toUpperCase() +
                dayPlaceholder?.slice(1)
              }
              isInvalid={
                props.isInvalid || (focused["day"] === false && !value.day)
              }
              onFocus={() => setFocused((curr) => ({ ...curr, day: true }))}
              onBlur={() =>
                setTimeout(() =>
                  setFocused((curr) => ({ ...curr, day: false }))
                )
              }
              isRequired={props.isRequired}
            />
          </div>
        );
      case "y":
        return (
          <div key={index} className={styles.year}>
            <DateSelectSegment
              testID={`${props.testID}-year`}
              isDisabled={props.isDisabled}
              selectedKey={value.year}
              input={input}
              setValue={handleSetYear}
              options={years}
              placeholder={
                yearPlaceholder?.charAt(0).toUpperCase() +
                yearPlaceholder?.slice(1)
              }
              isInvalid={
                props.isInvalid || (focused["year"] === false && !value.year)
              }
              onFocus={() => setFocused((curr) => ({ ...curr, year: true }))}
              onBlur={() =>
                setTimeout(() =>
                  setFocused((curr) => ({ ...curr, year: false }))
                )
              }
              isRequired={props.isRequired}
            />
          </div>
        );
    }
  };

  return (
    <Stack gap="space-1">
      <FormLabelRAC isRequired={props.isRequired}>{props.label}</FormLabelRAC>
      <div className={styles.wrapper}>
        {formatOrder.map((input: string, index: number) =>
          segmentLookup(input, index)
        )}
      </div>
      <FieldErrors>{props.errors}</FieldErrors>
    </Stack>
  );
}

const styles = {
  errorWrapper: css({ paddingTop: "var(--space-1)" }),
  wrapper: css({
    width: "100%",
    display: "flex",
    gap: "var(--space-2)",
  }),
  year: css({
    flex: "1.25",
  }),
  month: css({
    flex: "2",
  }),
};
